import { z } from 'zod';

const passwordSchema = z
  .string()
  .min(8, 'Password must have at least 8 characters')
  .max(100, 'Password must not have more than 100 characters')
  .refine((value) => /[a-z]/.test(value), {
    message: 'Password must have a least 1 lowercase letter',
  })
  .refine((value) => /[A-Z]/.test(value), {
    message: 'Password must have at least 1 uppercase letter',
  })
  .refine((value) => /\d/.test(value), {
    message: 'Password must have at least 1 number',
  })
  .refine((value) => /[!@#$%^&*(),.?":{}|<>]/.test(value), {
    message: 'Password must have at least 1 special character',
  });

export const MagicLinkSchema = z.object({
  email: z.string().email({
    message: 'Email is not valid',
  }),
});

export const PasswordLoginSchema = z.object({
  email: z.string().email({
    message: 'Email is not valid',
  }),
  password: z.string().min(1, {
    message: 'Password is required',
  }),
});

export const CorporateRegisterSchema = z.object({
  name: z.string().min(1, { message: 'First name is required' }),
  surname: z.string().min(1, { message: 'Last name is required' }),
  referredBy: z.string().min(1, { message: 'Referred By is required' }),
  companyName: z.string().min(1, { message: 'Company name is required' }),
  companyRegNo: z.string().regex(/^\d{4}\s*\/\s*\d{6}\s*\/\s*\d{2}$/, {
    message:
      'Company registration number must be in the format YYYY / DDDDDD / DD',
  }),

  email: z.string().email({
    message: 'Email is not valid',
  }),
  password: passwordSchema,
  mobileNumber: z
    .string()
    .regex(/^\+\d{8,15}$/, { message: 'Enter a valid phone number' }),
});

export const CorporateConfirmPasswordSchema = CorporateRegisterSchema.extend({
  confirmPassword: z.string().min(1, 'Confirm password is mandatory'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords must match',
  path: ['confirmPassword'],
});

export const ForgotPasswordSchema = z.object({
  identifier: z.string().email(),
});

export const ConfirmPasswordSchema = z
  .object({
    password: z
      .string()
      .min(6, { message: 'Password must be at least 6 characters long' }),
    confirmPassword: z
      .string()
      .min(6, { message: 'Password must be at least 6 characters long' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

export const GolfCourseSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  country: z.string().min(1, { message: 'Country is required' }),
  parThree: z
    .number({
      required_error: 'Required',
      invalid_type_error: 'Par-3 count must be a number',
    })
    .int({ message: 'Par-3 count must be an integer' })
    .refine((val) => [3, 4, 5].includes(val), {
      message: 'Par-3 count must be either 3, 4, or 5',
    }),
});

const STANDARD_LIKE_TYPES = ['standard', 'corporate'];

export const RewardTierSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    type: z.enum(['standard', 'corporate', 'custom', 'individual']),
    registrationFee: z
      .number({
        invalid_type_error: 'Registration fee must be a number',
      })
      .int('Registration fee must be an integer')
      .optional()
      .or(z.literal('')),
    prize: z
      .number({
        required_error: 'Prize is required',
        invalid_type_error: 'Prize must be a number',
      })
      .int('Prize must be an integer'),
    status: z.string().min(1, { message: 'Status is required' }),
    parThree: z
      .number({
        invalid_type_error: 'Par-3 count must be a number',
      })
      .int({ message: 'Par-3 count must be an integer' })
      .refine((val) => [3, 4, 5].includes(val), {
        message: 'Par-3 count must be either 3, 4, or 5',
      })
      .nullable()
      .optional(),
    playerCount: z
      .number({
        invalid_type_error: 'Player count must be a number',
      })
      .int({ message: 'Player count must be an integer' })
      .positive({ message: 'Player count must be greater than zero' })
      .nullable()
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (STANDARD_LIKE_TYPES.includes(data.type)) {
      if (data.parThree === undefined || data.parThree === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Par-3 count is required',
          path: ['parThree'],
        });
      }
      if (
        data.registrationFee === undefined ||
        data.registrationFee === '' ||
        Number(data.registrationFee) <= 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Registration fee must be greater than zero',
          path: ['registrationFee'],
        });
      }
    }

    if (data.type === 'individual') {
      if (
        data.registrationFee === undefined ||
        data.registrationFee === '' ||
        Number(data.registrationFee) <= 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Bundle price must be greater than zero',
          path: ['registrationFee'],
        });
      }
      if (
        data.playerCount === undefined ||
        data.playerCount === null ||
        Number(data.playerCount) <= 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Player count must be greater than zero',
          path: ['playerCount'],
        });
      }
    }
  });

export const ConfirmEmailSchema = z.object({
  email: z.string().email({
    message: 'Email is not valid',
  }),
});

export const UpdateVoucherValiditySchema = z
  .object({
    validFor: z.string().min(1, { message: 'Date is required' }),
  })
  .refine(
    (data) => {
      if (!data.validFor) {
        return true;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return new Date(data.validFor) >= today;
    },
    {
      message: 'Date cannot be in the past',
      path: ['validFor'],
    }
  );
